import { BindingEngine, computedFrom, observable } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { I18N } from "aurelia-i18n";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { QueriesClientService } from "../../../../../../../../services/queries/queries-client.services";
import { ConditionalDecimalPlacesValueConverter } from "../../../../converters/conditional-decimal-places-value-converter";
import * as moment from "moment";
import { IPaginationPage } from "../../../../../../../components/data-grid-pagination/data-grid-pagination";
import { VesselHeaderModel } from "../../../../../../../../models/vessels/vessel.models";
import { MepService } from "../../../../../../../../services/mep/mep.service";
import { LookupValue } from "../../../../../../../../models/lookups/lookup.models";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { MepLookupService } from "../../../../../../../../services/mep/mep-lookup.service";
import {Lookups} from "../../../../../../../../constants/mep/mep-lookup.constants";
import { MepLookupItem } from "../../../../../../../../models/mep/mep.models";
import * as Enumerable from "linq";
import { Reflection } from "../../../../../../../../utilities/reflection";

@autoinject()
@transient()
export class VesselPortActivityDialog implements IPaginationPage {

    private _model: any;
    public vesselData: any;
    public filters: any;
    public items: any[] = [];
    public scientificNotationConverter: ConditionalDecimalPlacesValueConverter = new ConditionalDecimalPlacesValueConverter();
    public lastPage: number;
    public count: number;
    public vm: VesselPortActivityDialog = this;
    public updatingCargo: boolean;
    public pageCountSet: any[] = [
        { label: 5, value: 5 },
        { label: 10, value: 10 },
        { label: 50, value: 50 },
        { label: 100, value: 100 }
    ];
    public selectedPageCount: number = 5;
    public supportsCargoType: boolean;
    public itemSelectedForEditing: any;
    public cargoTypeCategories: LookupValue[];
    public cargoTypes: MepLookupItem[];

    @observable()
    public currentPage: number = 1;

    @computedFrom(
        Reflection.getPropertyPath<VesselPortActivityDialog>(x => x.cargoTypes),
        Reflection.getPropertyPath<VesselPortActivityDialog>(x => x.itemSelectedForEditing),
        Reflection.getPropertyPath<VesselPortActivityDialog>(x => x.itemSelectedForEditing.cargoTypeGroup)
    )
    public get editingCargoTypes(): MepLookupItem[] {
        const editingCargoTypes =  this.itemSelectedForEditing ?
            this.cargoTypes.filter(x => x.additionalData.cargoTypeGroup == this.itemSelectedForEditing.cargoTypeGroup) :
            [];
        
        if (editingCargoTypes.every(x => x.id < 0)) {
            return [];
        }
        
        return editingCargoTypes;
    }

    @computedFrom(Reflection.getPropertyPath<VesselPortActivityDialog>(z => z.currentPage),
    Reflection.getPropertyPath<VesselPortActivityDialog>(z => z.selectedPageCount),
    Reflection.getPropertyPath<VesselPortActivityDialog>(z => z.count))
    public get displayingItemsText() {
        if (this.count > 0) {
            const firstItemIndexOnPage = ((this.currentPage - 1) * this.selectedPageCount) + 1;
            let lastItemIndexOnPage = firstItemIndexOnPage + this.selectedPageCount - 1;
            if (lastItemIndexOnPage > this.count) {
                lastItemIndexOnPage = this.count;
            }
            return this.i18N.tr('displaying-items-text').replace("{0}", firstItemIndexOnPage.toString()).replace("{1}", lastItemIndexOnPage.toString()).replace("{2}", this.count.toString()).replace("{3}", "Items");
        }
        else {
            return "";
        }
    }

    public get url(): string {
        const take = this.selectedPageCount;
        const skip = this.selectedPageCount * (this.currentPage - 1);
        return `odata/VesselPortActivities/Genesis.GetVesselPortActivities(portId=${this.filters.portId},vesselIds='${this.filters.vesselIds}',` +
            `emissionTypeIds='${this.filters.emissionTypeIds}',pointOfInterestIds='${this.filters.pointOfInterestIds}',` +
            `vesselTypeIds='${this.filters.vesselTypeIds}',operationModeIds='${this.filters.operationModeIds}',cargoTypeIds='${this.filters.cargoTypeIds}',` +
            `safetyScores='${this.filters.safetyScores}',ghgRatings='${this.filters.ghgRatings}',weightBucketIds='${this.filters.weightBucketIds}',` +
            `ageFrom=${this.filters.ageFrom},ageTo=${this.filters.ageTo},year=${this.filters.year},months='${this.filters.months}')?$top=${take}&$skip=${skip}&$count=true`;
    }

    constructor(protected itemsName: string = "Items", protected bindingEngine: BindingEngine,
        private dialogClientService: DialogClientService, private i18N: I18N, private queriesClientService: QueriesClientService,
        private mepService: MepService, private notificationClientService: NotificationClientService, private mepLookupService: MepLookupService) {

    }

    activate(model: {
        filters: {
            portId: number;
            vesselIds: string;
            emissionTypeIds: string;
            pointOfInterestIds: string;
            vesselTypeIds: string;
            operationModeIds: string;
            safetyScores: string;
            ghgRatings: string;
            weightBucketIds: string;
            ageFrom: number;
            ageTo: number;
            year: number;
            months: string;
            allowedEmissionTypes?: string;
        },
        vesselData: any,
        onClose: () => void
    }) {
        this._model = model;
        this.vesselData = model.vesselData;
        this.filters = model.filters;
        this.loadData();
    }

    nextClick(): void {
        this.currentPage++;
    }
    previousClick(): void {
        this.currentPage--;
    }

    currentPageChanged() {
        this.loadData();
    }

    public setPageCount(pageCount: { label: string, value: number }) {
        this.selectedPageCount = pageCount.value;
        this.loadData();
    }

    public convertKgToTonnes(value: number) {
        if (!value) return 0;
        const tonnesValue = value / 1000;
        return this.scientificNotationConverter.toView(tonnesValue, null)
    }

    public convertDuration(value: number) {
        if(!value) return "N/A";
        
        let result = ''; 
        const dayInMinutes = 1440; 
        const hourInMinutes = 60;
        const days = Math.floor(value/dayInMinutes);
        const hours = Math.floor((value%dayInMinutes)/hourInMinutes);
        const mins = Math.floor((value%dayInMinutes)%hourInMinutes);

        if (days > 0) {
            result += days > 1 ? `${days}days ` : `${days}day `;
        }

        if (hours > 0) {
            result += `${hours}hr `;
        }

        if (mins > 0) {
            result += `${mins}min`;
        }

        return result.trim();
    }

    formatNumber(value: number) {
        return String(value).padStart(2, '0');
    }

    public convertTime(value: Date) {
        if (!value) return "N/A";
        return moment(value).format("DD MMM YYYY HH:mm");
    }

    public convertCargoType(value: string, activity_MemberId: number) {
        if (!value || activity_MemberId < 0) return "N/A";
        return value;
    }

    public convertCargoQuantity(value: number, activity_MemberId: number) {
        if (!value || activity_MemberId < 0) return "N/A";
        return `${value} tonnes`;
    }

    getVesselHeaderModel = () => {
        return new Promise<VesselHeaderModel>((resolve) => {
            resolve({
                isVerified: this.vesselData.isGhgVerified,
                emissionsRating: this.vesselData.ghgRating,
                emissionsRatingPlus: this.vesselData.isGhgRatingPlus,
                vesselType: this.vesselData.vesselTypeGroup,
                dwt: this.vesselData.dwt,
                maxDwt: this.vesselData.dwt,
                starRating: this.vesselData.safetyScore,
                vesselId: this.vesselData.vesselSKey,
                entityId: this.vesselData.vesselSKey,
                imo: this.vesselData.imo,
                vesselName: this.vesselData.vesselName,
                age: this.vesselData.age,
                isSanctioned: null,
                rsInspectionRequired: null,
                vesselStatus: null,
                vesselStatusCode: null,
                isInGeoPoliticalZone: null,
                hasGeopoliticalConflict: null,
                flag: null,
                dateOfBuild: null,
                lastRightShipInspectionId: null,
                requestedDateOfInspection: null,
                rightShipInspectionExpiryDate: null,
                reasonForInspection: null,
                actualDateOfInspection: null,
                safetyRatingType: null,
                notInService: null,
                rightShipInspectionStatus: null,
                requiresRightShipInspection: null,
                classSocieties: null,
                banners: null,
                daysRemainingMinutely: null,
                daysRemaining: null,
                daysToAgeRequirement: null,
                inspectionState: null,
                inspectionRequired: null,
                eni: null,
                vin: null,
                label: null,
                identifier: null,
                officialNumber: null,
                hasPendingInspection: null,
                rightShipInspectionGradeCode: null,
                rightShipInspectionGradeToolTipText: null,
                rightShipInspectionGrade: null,
                useMobileInspectionWorkflow: null,
                isRightShipInspectionStatusAcceptable: null,
                isEexiCompliant: null,
                eexiComplianceTypeId: null,
                hideSafetyScore: true,
                hasCompletedVesselHealthCheck: false,
                lastVhcPerformedDate: null,
                ciiRating: null,
                incidentGracePeriods: null,
                isFlagFake: null
            });
        });
    }

    close() {
        this._model.onClose();
        this.dialogClientService.hide();
    }

    save() {
        return new Promise<void>((resolve, reject) => {
            this.mepService.updateCargoData({
                portId: this._model.filters.portId,
                year: this._model.filters.year,
                activity_MemberId: this.itemSelectedForEditing.activity_MemberId,
                cargoType: this.itemSelectedForEditing.cargoType,
                cargoQuantity: this.itemSelectedForEditing.cargoQuantity
            }).then(() => {
                this.itemSelectedForEditing.editing = false;
                this.notificationClientService.show(``, `Port activity saved successfully`, NotificationType.Success);
                resolve();
            }).catch(() => {
                this.notificationClientService.show(``, `Port activity failed to save`, NotificationType.Error);
                reject();
            });
        });
    }

    private loadData() {
        if (this.filters) {
            Promise.all([this.queriesClientService.getData(this.url),
            this.mepService.getMepPortDetails(this.filters.portId, true),
            this.loadCargoTypes()]).then(response => {
                this.items = response[0].value.map((model) => {
                    return { ...model, editing: false };
                });
                this.count = response[0]["@odata.count"] as number;
                this.lastPage = Math.ceil(this.count / (this.selectedPageCount || this.pageCountSet[0].value));
                this.supportsCargoType = response[1].supportsCargoType;
            });
        }
    }

    loadCargoTypes() {
        return new Promise<void>((resolve, reject) => {
            this.mepLookupService.getLookups({
                portId: this.filters.portId,
                queries: [Lookups.CargoTypes],
                emissionTypesIncludeAllYears: false,
                years: [0]
            }).then(response => {
                if (response.hasError) {
                    reject();
                } else {
                    this.cargoTypes = Enumerable.from(response.allValuesByQuery[Lookups.CargoTypes]).where(x => x.name !== 'UNSPECIFIED').toArray();
                    const na: MepLookupItem = {id: -1, name: "", displayName: "", additionalData: {cargoTypeGroup: ""}, isDefault: false};
                    this.cargoTypes.unshift(na);
                    this.cargoTypeCategories = Enumerable.from(this.cargoTypes)
                        .select(x => x.additionalData.cargoTypeGroup)
                        .distinct()
                        .select((x, index) => (<MepLookupItem>{
                            additionalData: null,
                            displayName: x,
                            name: x,
                            id: index + 1,
                            isDefault: false
                        })).toArray();
                }
                resolve(null);
            }).catch(() => {
                reject();
            })
        })

    }

    togglePortCallDetailsEditing(item: any) {

        //Set all other items to editing false
        this.items.forEach(x => x.editing = false);
        this.itemSelectedForEditing = item;

        //Set item as editing true
        this.itemSelectedForEditing.editing = true;
    }
}